import { Box, Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel } from '@mui/material';
import { ChangeEvent, ChangeEventHandler, useEffect, useMemo, useState } from 'react';
import useStyles from './styles';

interface CheckboxesOption {
  name: string;
  label: string;
  checked?: boolean;
}

interface CheckboxGroupProps {
  label: string;
  options: CheckboxesOption[];
  onChange: (selectedOptions: Record<string, boolean>) => void;
  helperText?: string;
  required?: boolean;
  error?: boolean;
  minSelected?: number;
  maxSelected?: number;
  layout?: 'horizontal' | 'vertical';
}

export default function CheckboxGroup({
  label,
  onChange,
  options: initialOptions,
  error,
  helperText,
  maxSelected,
  minSelected,
  required,
  layout = 'vertical',
}: CheckboxGroupProps) {
  const [options, setOptions] = useState(initialOptions);
  const styles = useStyles({ layout });

  useEffect(() => {
    setOptions(initialOptions);
  }, [initialOptions]);

  const selectedOptions = useMemo(
    () =>
      options.reduce(
        (acc, option) => ({
          ...acc,
          [option.name]: option.checked || false,
        }),
        {} as Record<string, boolean>
      ),
    [options]
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const newOptions = options.map((option) =>
      option.name === event.target.name ? { ...option, checked: event.target.checked } : option
    );

    setOptions(newOptions);

    const newSelected = {
      ...selectedOptions,
      [event.target.name]: event.target.checked,
    };

    onChange(newSelected);
  };

  const validationError = useMemo(() => {
    const selectedCount = Object.values(selectedOptions).filter(Boolean).length;

    if (minSelected !== undefined && selectedCount < minSelected) return true;
    if (maxSelected !== undefined && selectedCount > maxSelected) return true;

    return false;
  }, [selectedOptions, minSelected, maxSelected]);

  return (
    <Box sx={{ display: 'flex' }}>
      <FormControl
        className={styles.formControlError}
        required={required}
        error={error || validationError}
        component="fieldset"
        variant="standard"
      >
        <FormLabel className={styles.formLabelError} component="legend">
          {label}
        </FormLabel>
        <FormGroup className={styles.formGroup}>
          {options.map((option) => (
            <FormControlLabel
              key={option.name}
              control={
                <Checkbox
                  checked={selectedOptions[option.name] || false}
                  onChange={handleChange}
                  name={option.name}
                  className={styles.checkboxError}
                />
              }
              label={option.label}
            />
          ))}
        </FormGroup>
        {helperText && (
          <FormHelperText className={styles.helperTextError}>
            {helperText}
            {minSelected && ` (Mínimo: ${minSelected})`}
            {maxSelected && ` (Máximo: ${maxSelected})`}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
}
